// Settings › Price book › Option templates: saved good / better / best option sets an estimate starts
// from, in the office's job sheet and on the iPad. Kept as price-book items and quantities; each
// option shows what it comes to at today's prices. The price book's people change them (pricebook.edit).

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Loading } from '../shared/Loading.tsx';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { money } from '../review/review-model.ts';
import { PricebookPicker } from '../job/PricebookPicker.tsx';
import {
  blankTplOption,
  emptyTemplate,
  MAX_LINES,
  MAX_OPTIONS,
  templateForm,
  templateInput,
  templateScope,
  TIERS,
  tplLine,
  tplOptionCents,
  type OptionTemplate,
  type Tier,
  type TplForm,
  type TplOptionForm,
} from './option-templates-model.ts';
import './setup.css';

interface Named {
  id: string;
  name: string;
  businessUnitId?: string | null;
}

export function OptionTemplatesCard({ canEdit }: { canEdit: boolean }) {
  const [rows, setRows] = useState<OptionTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [units, setUnits] = useState<Named[]>([]);
  const [types, setTypes] = useState<Named[]>([]);
  const [open, setOpen] = useState<OptionTemplate | 'new' | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setRows(
        (
          await client.call<{ templates: OptionTemplate[] }>('optionTemplates.list', {
            includeRetired: canEdit,
          })
        ).templates,
      );
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, [canEdit]);
  useEffect(() => {
    void load();
    client
      .call<{ businessUnits: Named[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{ jobTypes: Named[] }>('jobTypes.list', {})
      .then((r) => setTypes(r.jobTypes))
      .catch(() => undefined);
  }, [load]);
  const done = (text: string) => {
    setOpen(null);
    setSaid(text);
    void load();
  };
  return (
    <section className="sheet setup-card" aria-labelledby="tpl-opt-title">
      <div className="side-head">
        <h2 id="tpl-opt-title">Option templates</h2>
        {canEdit && (
          <button className="btn primary" onClick={() => setOpen('new')}>
            Add a template
          </button>
        )}
      </div>
      <p className="muted small">
        Good, better and best options an estimate can start from, in the job sheet and on the iPad. They keep
        the items and quantities; prices come from the price book when they’re used.{' '}
        {canEdit ? '' : 'Only the owner or a manager changes these.'}
      </p>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {!rows ? (
        <Loading error={error} what="the option templates" retry={() => void load()} />
      ) : !rows.length ? (
        <p className="muted">No option templates yet.</p>
      ) : (
        <ul className="setup-list">
          {rows.map((t) => (
            <li key={t.id} className={t.active ? '' : 'off'}>
              <div>
                <b>{t.name}</b>
                <span className="muted small"> · {templateScope(t, units, types)}</span>
                {!t.active && <span className="chip say off"> Retired</span>}
                <p className="muted small">
                  {t.options.map((o) => `${o.title} ${money(o.totalCents)}`).join(' · ')}
                </p>
              </div>
              {canEdit && (
                <button className="btn quiet sm" aria-label={`Edit ${t.name}`} onClick={() => setOpen(t)}>
                  Edit
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <TemplateSheet
          template={open === 'new' ? null : open}
          units={units}
          types={types}
          onDone={done}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

function TemplateSheet({
  template,
  units,
  types,
  onDone,
  onClose,
}: {
  template: OptionTemplate | null;
  units: Named[];
  types: Named[];
  onDone: (said: string) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState<TplForm>(() => (template ? templateForm(template) : emptyTemplate()));
  const w = useWrite(JSON.stringify(f));
  const life = useWrite(`life:${template?.id ?? ''}`);
  const set = (patch: Partial<TplForm>) => {
    setF({ ...f, ...patch });
    w.setError(null);
  };
  const setO = (i: number, patch: Partial<TplOptionForm>) =>
    set({ options: f.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const typesHere = types.filter(
    (t) => !f.businessUnitId || !t.businessUnitId || t.businessUnitId === f.businessUnitId,
  );
  const save = async () => {
    const r = await w.run<OptionTemplate>(
      template ? 'optionTemplates.update' : 'optionTemplates.create',
      templateInput(template, f),
    );
    if (r) onDone(template ? `${r.name} is saved.` : `${r.name} is added. Estimates can start from it.`);
  };
  const flip = async () => {
    if (!template) return;
    const r = await life.run<OptionTemplate>(
      template.active ? 'optionTemplates.retire' : 'optionTemplates.restore',
      {
        input: { id: template.id, rowVersion: template.rowVersion },
      },
    );
    if (r)
      onDone(
        r.active
          ? `${r.name} is offered again.`
          : `${r.name} is retired. Estimates already started from it keep what they have.`,
      );
  };
  return (
    <Sheet title={template ? template.name : 'Add an option template'} wide onClose={onClose}>
      <label className="f">
        Name
        <input
          className="field"
          value={f.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g. Water heater replacement"
          autoFocus
        />
      </label>
      <div className="fgrid">
        <label className="f">
          Department
          <select
            className="field"
            value={f.businessUnitId}
            onChange={(e) => set({ businessUnitId: e.target.value, jobTypeId: '' })}
          >
            <option value="">Every department</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          Job type
          <select className="field" value={f.jobTypeId} onChange={(e) => set({ jobTypeId: e.target.value })}>
            <option value="">Every job type</option>
            {typesHere.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ol className="question-list tpl-options">
        {f.options.map((o, i) => {
          const n = i + 1;
          const total = tplOptionCents(o);
          return (
            <li key={i} className="question" aria-label={`Option ${n}`}>
              <div className="fgrid">
                <label className="f">
                  Option {n} title
                  <input
                    className="field"
                    value={o.title}
                    onChange={(e) => setO(i, { title: e.target.value })}
                  />
                </label>
                <label className="f">
                  Tier
                  <select
                    className="field"
                    value={o.tier}
                    onChange={(e) => setO(i, { tier: e.target.value as Tier | '' })}
                  >
                    {TIERS.map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="f">
                What the customer reads (optional)
                <textarea
                  className="field area"
                  value={o.summary}
                  maxLength={2000}
                  onChange={(e) => setO(i, { summary: e.target.value })}
                />
              </label>
              {o.lines.length > 0 && (
                <ul className="tpl-lines" aria-label={`Option ${n} lines`}>
                  {o.lines.map((l, j) => (
                    <li key={`${l.pricebookItemId}-${j}`} className={l.active ? '' : 'off'}>
                      <span className="who-col">
                        <b>{l.name ?? 'An item no longer in the price book'}</b>{' '}
                        <span className="muted small">{l.code}</span>
                        {!l.active && <span className="chip say warn"> No longer offered</span>}
                      </span>
                      <label className="qty">
                        <input
                          className="field num"
                          inputMode="decimal"
                          aria-label={`Quantity of ${l.name ?? 'this item'}`}
                          value={l.quantity}
                          onChange={(e) =>
                            setO(i, {
                              lines: o.lines.map((x, k) =>
                                k === j ? { ...x, quantity: e.target.value } : x,
                              ),
                            })
                          }
                        />
                      </label>
                      <span className="amt">{l.unitPriceCents === null ? '—' : money(l.unitPriceCents)}</span>
                      <button
                        type="button"
                        className="btn quiet sm"
                        aria-label={`Remove ${l.name ?? 'this line'} from option ${n}`}
                        onClick={() => setO(i, { lines: o.lines.filter((_, k) => k !== j) })}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {o.lines.length < MAX_LINES && (
                <PricebookPicker onPick={(item) => setO(i, { lines: [...o.lines, tplLine(item)] })} />
              )}
              <div className="row q-acts">
                <span className="muted small tpl-total">
                  {total === null ? 'Check the quantities' : `At today’s prices: ${money(total)}`}
                </span>
                <button
                  type="button"
                  className="btn quiet sm"
                  aria-label={`Remove option ${n}`}
                  disabled={f.options.length === 1}
                  onClick={() => set({ options: f.options.filter((_, k) => k !== i) })}
                >
                  Remove option
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      {f.options.length < MAX_OPTIONS && (
        <button
          type="button"
          className="btn soft"
          onClick={() => set({ options: [...f.options, blankTplOption(f.options.length)] })}
        >
          Add an option
        </button>
      )}
      <p className="muted small">
        Prices aren’t kept in the template: an estimate started from it uses the price book’s prices that day.
      </p>
      <SheetButtons
        w={w}
        label={template ? 'Save' : 'Add template'}
        busyLabel="Saving…"
        onSave={() => void save()}
        onCancel={onClose}
      />
      {template && (
        <div className="row life">
          <button type="button" className="btn soft" disabled={life.busy} onClick={() => void flip()}>
            {life.busy
              ? template.active
                ? 'Retiring…'
                : 'Bringing it back…'
              : template.active
                ? 'Retire this template'
                : 'Offer it again'}
          </button>
          {life.error && (
            <p className="error small" role="alert">
              {life.error}
            </p>
          )}
        </div>
      )}
    </Sheet>
  );
}
